import { z } from "zod";

export const integrationFeatureScheduleSchema = z.object({
  scheduleName: z.string(),
  cronExpression: z.string(),
  metadata: z.record(z.unknown()).optional(),
  createdDate: z.number().optional(),
  updatedDate: z.number().optional(),
});

export const integrationFeatureSchema = z.object({
  featureName: z.string(),
  metadata: z.record(z.unknown()).optional(),
  featureConfig: z
    .object({
      regions: z.union([z.string(), z.array(z.string())]).transform((val) => {
        if (Array.isArray(val)) {
          return val.join(",");
        }
        return val;
      }),
    })
    .nullish(),
  schedules: z.array(integrationFeatureScheduleSchema).optional(),
});

export const integrationSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  integrationType: z.string(),
  isPrivateApp: z.boolean().optional(),
  isEnabled: z.boolean().optional(),
  connectionStatus: z.enum(["VALID", "INVALID", "UNKNOWN"]),
  features: z.array(integrationFeatureSchema).optional(),
  createdDate: z.number().optional(),
  updatedDate: z.number().optional(),
  metadata: z.record(z.unknown()).optional(),
  // Present on child integrations of an org onboarding. `scopeType` distinguishes
  // structural nodes (e.g. "org_unit") from leaf accounts (e.g. "account");
  // `parentIntegrationId` links a child to its parent to reconstruct the tree.
  scopeType: z.string().optional(),
  parentIntegrationId: z.number().optional(),
});

export const integrationsResponseSchema = z.object({
  integrations: z.array(integrationSchema),
  nextPageToken: z.string().optional(),
});

export const integrationCredentialsSchema = z.object({
  type: z.string(),
  data: z
    .object({
      roleArn: z.string().optional(),
      externalId: z.string().optional(),
      service_account_json: z.string().optional(),
      clientId: z.string().optional(),
      clientSecret: z.string().optional(),
    })
    .catchall(z.unknown()),
  metadata: z
    .object({
      description: z.string().optional(),
      domain: z.string().optional(),
      dataverseUrl: z.string().optional(),
      baseUrl: z.string().optional(),
    })
    .optional(),
});

export const integrationTypeFeatureSchema = z.object({
  featureId: z.number().nullable(),
  featureName: z.string(),
  description: z.string().nullable(),
  isEnabled: z.boolean().nullable(),
});

export const integrationTypeFeaturesResponseSchema = z.object({
  integrationType: z.string().optional(),
  features: z.array(integrationTypeFeatureSchema),
});

export const testConnectionResponseSchema = z.object({
  status: z.enum(["VALID", "INVALID", "UNKNOWN", "ACTIVE"]),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

export const featureResourceTypeSchema = z.object({
  id: z.number(),
  resourceTypeCode: z.string(),
  resourceTypeName: z.string(),
  serviceName: z.string(),
  description: z.string(),
  isActive: z.boolean(),
  requiredPermissions: z.union([z.record(z.unknown()), z.array(z.unknown())]),
});

export const featureResourceTypesResponseSchema = z.object({
  resourceTypes: z.array(featureResourceTypeSchema),
});

export type IntegrationTypeFeature = z.infer<
  typeof integrationTypeFeatureSchema
>;
export type IntegrationTypeFeaturesResponse = z.infer<
  typeof integrationTypeFeaturesResponseSchema
>;
export type TestConnectionResponse = z.infer<
  typeof testConnectionResponseSchema
>;
export type FeatureResourceType = z.infer<typeof featureResourceTypeSchema>;
export type FeatureResourceTypesResponse = z.infer<
  typeof featureResourceTypesResponseSchema
>;

// Use z.output to get the type after transformations (e.g., regions: string | string[] -> string)
export type Integration = z.output<typeof integrationSchema>;
export type IntegrationsResponse = z.output<typeof integrationsResponseSchema>;
export type IntegrationCredentials = z.infer<
  typeof integrationCredentialsSchema
>;

// Permission Validation API
export const resourceTypeResultSchema = z.object({
  code: z.string(),
  name: z.string(),
  status: z.enum(["GRANTED", "MISSING", "UNCHECKED"]),
});

export const validatePermissionsResponseSchema = z.object({
  integrationType: z.string(),
  status: z.enum(["GRANTED", "PARTIAL"]),
  validatedAt: z.string().optional(),
  summary: z.object({
    total: z.number(),
    granted: z.number(),
    missing: z.number(),
  }),
  resourceTypes: z.array(resourceTypeResultSchema),
});

export type ResourceTypeResult = z.infer<typeof resourceTypeResultSchema>;
export type ValidatePermissionsResponse = z.infer<
  typeof validatePermissionsResponseSchema
>;
